import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { businessSlice } from "../../../../../../features/business/businessSlice";
import ProtectedPaymentRoute from "../../../../../auth/Auth";

export default function Member() {
  ProtectedPaymentRoute();

  const actions = businessSlice.actions;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = new URLSearchParams(location.search).get("edit") === "true";

  const savedBusinessMembers = useSelector(
    (state) => state.business.businessMembers[0]
  );
  const business = useSelector((state) => state.business);
  const businessTypeId = business?.businessTypeId;

  const [businessMembers, setBusinessMembers] = useState({
    firstName: savedBusinessMembers?.firstName || "",
    lastName: savedBusinessMembers?.lastName || "",
    email: savedBusinessMembers?.email || "",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  useEffect(() => {
    // hook kept for future edit-mode specific behavior
  }, [location.search]);

  const handleInputChange = (field, value) => {
    setBusinessMembers((prev) => ({ ...prev, [field]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleNextButtonClick = (event) => {
    event.preventDefault();

    let isValid = true;
    const updatedErrors = {};

    // Validate First Name
    if (!businessMembers.firstName.trim()) {
      updatedErrors.firstName = "Please enter first name";
      isValid = false;
    } else if (!/^[a-zA-Z\s]+$/.test(businessMembers.firstName)) {
      updatedErrors.firstName = "Please enter a valid first name";
      isValid = false;
    }

    // Validate Last Name
    if (!businessMembers.lastName.trim()) {
      updatedErrors.lastName = "Please enter last name";
      isValid = false;
    } else if (!/^[a-zA-Z\s]+$/.test(businessMembers.lastName)) {
      updatedErrors.lastName = "Please enter a valid last name";
      isValid = false;
    }

    // Validate Email
    if (!businessMembers.email.trim()) {
      updatedErrors.email = "Please enter email address";
      isValid = false;
    } else if (!isValidEmail(businessMembers.email.trim())) {
      updatedErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    setErrors(updatedErrors);

    if (isValid) {
      dispatch(
        actions.update_business_members({
          firstName: businessMembers.firstName,
          lastName: businessMembers.lastName,
          email: businessMembers.email,
        })
      );

      if (isEditMode) {
        navigate("/invoice");
      } else {
        if (businessTypeId === 1) {
          navigate("/llc_complian");
        } else {
          navigate("/product_service");
        }
      }
    }
  };

  const handleNextPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleNextButtonClick(event);
    }
  };

  const handleBackButtonClick = () => {
    navigate("/business_type");
  };

  return (
    <div className="min-h-[75vh] bg-white py-12 lg:py-20 font-sans flex items-center">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          
          {/* Left Graphic */}
          <div className="lg:col-span-5 flex justify-center items-center">
            <img 
              src="/Assets/Form_img/people.png" 
              alt="LLC Members" 
              className="w-full max-w-[340px] h-auto object-contain"
            />
          </div>

          {/* Right Form */}
          <div className="lg:col-span-7">
            <div className="mb-8">
              <span className="text-[#1a73e8] font-medium tracking-wider uppercase text-xs mb-2 block">
                Ownership & Management
              </span>
              <h1 className="text-3xl sm:text-4xl font-normal text-[#202124] tracking-tight mb-3">
                Add your member details
              </h1>
              <p className="text-[15px] text-[#5f6368] leading-relaxed">
                Provide details for the managing member or owner who will be registered on the LLC filings.
              </p>
            </div>

            <form onKeyDown={handleNextPress} className="space-y-5 max-w-xl">
              {/* First Name & Last Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[14px] font-medium text-[#3c4043] mb-2">
                    First Name <span className="text-[#d93025] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Sarah"
                    value={businessMembers.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    className={`w-full h-[48px] px-4 text-[15px] rounded-[6px] border ${
                      errors.firstName ? "border-[#d93025] focus:border-[#d93025] focus:ring-[#d93025]/20" : "border-[#dadce0] focus:border-[#1a73e8] focus:ring-[#1a73e8]/20"
                    } focus:ring-2 outline-none transition-all placeholder:text-[#9aa0a6] text-[#202124] bg-white`}
                  />
                  {errors.firstName && (
                    <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                      <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                      {errors.firstName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[14px] font-medium text-[#3c4043] mb-2">
                    Last Name <span className="text-[#d93025] font-semibold">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Jenkins"
                    value={businessMembers.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    className={`w-full h-[48px] px-4 text-[15px] rounded-[6px] border ${
                      errors.lastName ? "border-[#d93025] focus:border-[#d93025] focus:ring-[#d93025]/20" : "border-[#dadce0] focus:border-[#1a73e8] focus:ring-[#1a73e8]/20"
                    } focus:ring-2 outline-none transition-all placeholder:text-[#9aa0a6] text-[#202124] bg-white`}
                  />
                  {errors.lastName && (
                    <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                      <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-[14px] font-medium text-[#3c4043] mb-2">
                  Member Email Address <span className="text-[#d93025] font-semibold">*</span>
                </label>
                <input
                  type="email"
                  placeholder="e.g. sarah.jenkins@example.com"
                  value={businessMembers.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={`w-full h-[48px] px-4 text-[15px] rounded-[6px] border ${
                    errors.email ? "border-[#d93025] focus:border-[#d93025] focus:ring-[#d93025]/20" : "border-[#dadce0] focus:border-[#1a73e8] focus:ring-[#1a73e8]/20"
                  } focus:ring-2 outline-none transition-all placeholder:text-[#9aa0a6] text-[#202124] bg-white`}
                />
                {errors.email && (
                  <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                    <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-4 pt-6">
                {!isEditMode && (
                  <button
                    type="button"
                    onClick={handleBackButtonClick}
                    className="px-6 py-2.5 rounded-[4px] border border-[#dadce0] text-[#1a73e8] font-medium hover:bg-[#f8f9fa] transition-colors flex items-center gap-2 cursor-pointer text-[14px]"
                  >
                    <i className="fa-solid fa-arrow-left text-xs"></i> Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleNextButtonClick}
                  className="px-8 py-2.5 rounded-[4px] bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium shadow-sm transition-all flex items-center gap-2 cursor-pointer text-[14px]"
                >
                  {isEditMode ? "Update Member" : "Next"} <i className="fa-solid fa-arrow-right text-xs"></i>
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
}
